import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Tabs,
  Descriptions,
  Timeline,
  Table,
  Upload,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Modal,
  Select,
} from "antd";
import {
  UploadOutlined,
  RobotOutlined,
  FileTextOutlined,
  DeleteOutlined,
  PrinterOutlined,
  EyeOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import {
  caseAPI,
  partyAPI,
  eventAPI,
  documentAPI,
  evidenceAPI,
  aiAPI,
} from "../services/api";
import { Case, Party, Event, Document, Evidence } from "../types";
import dayjs from "dayjs";

const { TabPane } = Tabs;

function getReportHtml(title: string, jsonB64: string): string {
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    @page { margin: 2cm; }
    * { box-sizing: border-box; }
    body {
      font-family: "Georgia", "Times New Roman", serif;
      font-size: 11pt;
      line-height: 1.5;
      color: #1a1a1a;
      max-width: 21cm;
      margin: 0 auto;
      padding: 2rem;
    }
    .report-header {
      border-bottom: 2px solid #1a365d;
      padding-bottom: 1rem;
      margin-bottom: 2rem;
    }
    .report-header h1 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #1a365d;
      margin: 0 0 0.25rem 0;
    }
    .report-header .meta {
      font-size: 0.85rem;
      color: #666;
    }
    .section {
      margin-bottom: 2rem;
      page-break-inside: avoid;
    }
    .section h2 {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1a365d;
      margin: 0 0 0.75rem 0;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid #e2e8f0;
    }
    .section-content {
      text-align: justify;
    }
    .section-content p { margin: 0 0 0.75rem 0; }
    .section-content ul {
      margin: 0.5rem 0 1rem 1.5rem;
      padding: 0;
    }
    .section-content li { margin-bottom: 0.5rem; }
    .section-content strong { color: #2d3748; }
    .na { color: #999; font-style: italic; }
    @media print {
      body { padding: 0; }
      .section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="report-header">
    <h1>Informe de Asesoramiento Legal</h1>
    <p class="meta">${title} &bull; Generado: ${new Date().toLocaleString("es-ES")}</p>
  </div>
  <div id="content"></div>
  <script>
    const data = JSON.parse(decodeURIComponent(escape(atob("${jsonB64}"))));
    const el = document.getElementById("content");
    function escapeHtml(s) {
      if (typeof s !== "string") return s;
      return s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
    }
    function render(obj, inList) {
      if (obj === null || obj === undefined) return "<span class='na'>N/A</span>";
      if (typeof obj === "string") {
        const text = escapeHtml(obj).replace(/\\n/g, "</p><p>");
        return inList ? text : "<p>" + text + "</p>";
      }
      if (typeof obj === "number" || typeof obj === "boolean") return "<span>" + String(obj) + "</span>";
      if (Array.isArray(obj)) {
        if (obj.length === 0) return "<p class='na'>Ninguno</p>";
        return "<ul>" + obj.map(i => "<li>" + render(i, true) + "</li>").join("") + "</ul>";
      }
      if (typeof obj === "object") {
        let h = "";
        for (const [k, v] of Object.entries(obj)) {
          if (k === "raw_response" || k === "document_ids" || k === "document_titles") continue;
          const val = render(v, false);
          h += "<div class='section'><h2>" + escapeHtml(k) + "</h2><div class='section-content'>" + val + "</div></div>";
        }
        return h;
      }
      return "<span>" + escapeHtml(String(obj)) + "</span>";
    }
    el.innerHTML = render(data);
  </script>
</body>
</html>`;
}

const CaseView: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [case_, setCase] = useState<Case | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [documentAnalysis, setDocumentAnalysis] = useState<any>(null);
  const [analyzingDocument, setAnalyzingDocument] = useState<number | null>(
    null
  );
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [documentAnalyses, setDocumentAnalyses] = useState<
    Map<number, boolean>
  >(new Map()); // Track which documents have saved analyses
  const [selectedDocuments, setSelectedDocuments] = useState<React.Key[]>([]); // Selected document IDs for combined analysis
  const [loading, setLoading] = useState(true);
  const [copyingDocId, setCopyingDocId] = useState<number | null>(null);
  const [copyingAllPdfs, setCopyingAllPdfs] = useState(false);

  useEffect(() => {
    if (caseId) {
      fetchCaseData(parseInt(caseId));
    }
  }, [caseId]);

  const fetchCaseData = async (id: number) => {
    try {
      const [caseData, partiesData, eventsData, docsData, evidenceData] =
        await Promise.all([
          caseAPI.getById(id),
          partyAPI.getByCaseId(id),
          eventAPI.getByCaseId(id),
          documentAPI.getByCaseId(id),
          evidenceAPI.getByCaseId(id),
        ]);

      setCase(caseData);
      setParties(partiesData);
      setEvents(eventsData);
      setDocuments(docsData);
      setEvidence(evidenceData);

      // Check which documents have saved analyses
      const analysesMap = new Map<number, boolean>();
      for (const doc of docsData) {
        try {
          await documentAPI.getSavedAnalysis(doc.document_id);
          analysesMap.set(doc.document_id, true);
        } catch {
          analysesMap.set(doc.document_id, false);
        }
      }
      setDocumentAnalyses(analysesMap);
    } catch (error) {
      console.error("Error fetching case data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!caseId) return;
    try {
      message.loading({
        content: "Ejecutando análisis de IA...",
        key: "ai-analysis",
        duration: 0,
      });
      const [risk, omissions, precedents] = await Promise.all([
        aiAPI.getRiskAnalysis(parseInt(caseId)),
        aiAPI.getOmissions(parseInt(caseId)),
        aiAPI.getPrecedents(parseInt(caseId)),
      ]);
      setAiAnalysis({ risk, omissions, precedents });
      message.success({ content: "Análisis completado", key: "ai-analysis" });
    } catch (error: any) {
      console.error("Error running AI analysis:", error);
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Error desconocido al ejecutar el análisis";
      if (error?.response?.status === 429) {
        const detail = error?.response?.data?.detail || "";
        if (detail.includes("quota") || detail.includes("billing")) {
          message.error({
            content:
              "Cuota de OpenAI agotada. Por favor, verifica tu plan y facturación en OpenAI, o configura Ollama para usar IA local gratuita.",
            key: "ai-analysis",
            duration: 10,
          });
        } else {
          message.error({
            content:
              "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
            key: "ai-analysis",
            duration: 5,
          });
        }
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "ai-analysis",
          duration: 5,
        });
      }
    }
  };

  const handleCombinedAnalysis = async (documentIds: React.Key[]) => {
    if (documentIds.length === 0) {
      message.warning("Por favor, selecciona al menos un documento");
      return;
    }

    setAnalyzingDocument(-1); // Use -1 to indicate combined analysis
    try {
      const docTitles = documents
        .filter((d) =>
          documentIds.some((k) => Number(k) === d.document_id)
        )
        .map((d) => d.title)
        .join(", ");

      message.loading({
        content: `Analizando ${documentIds.length} documento(s) combinado(s)...`,
        key: "combined-analysis",
        duration: 0,
      });

      const analysis = await documentAPI.analyzeCombined(
        documentIds.map((id) => Number(id))
      );

      if (analysis.error) {
        if (
          analysis.error_type === "quota_exceeded" ||
          analysis.error_type === "rate_limit"
        ) {
          message.error({
            content:
              analysis.error ||
              "Cuota de API agotada. Por favor, verifica tu configuración.",
            key: "combined-analysis",
            duration: 5,
          });
        } else {
          message.error({
            content: analysis.error || "Error al analizar los documentos",
            key: "combined-analysis",
            duration: 5,
          });
        }
        setAnalyzingDocument(null);
        return;
      }

      setDocumentAnalysis({
        documentId: -1,
        documentTitle: `Análisis Combinado: ${docTitles}`,
        analysis,
      });
      setAnalysisModalVisible(true);
      setSelectedDocuments([]); // Clear selection after analysis

      message.success({
        content: "Análisis combinado completado",
        key: "combined-analysis",
      });
    } catch (error: any) {
      console.error("Error analyzing combined documents:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Error desconocido";
      if (error?.response?.status === 429) {
        message.error({
          content:
            "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
          key: "combined-analysis",
          duration: 5,
        });
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "combined-analysis",
          duration: 5,
        });
      }
    } finally {
      setAnalyzingDocument(null);
    }
  };

  const handleDocumentAnalysis = async (
    documentId: number,
    documentTitle: string,
    useSaved: boolean = false
  ) => {
    setAnalyzingDocument(documentId);
    try {
      let analysis;

      if (useSaved) {
        // Load saved analysis
        message.loading({
          content: `Cargando análisis guardado de ${documentTitle}...`,
          key: "doc-analysis",
          duration: 0,
        });
        const saved = await documentAPI.getSavedAnalysis(documentId);
        analysis = saved.results;
        analysis.analysis_id = saved.analysis_id;
        analysis.saved_at = saved.analysis_date;
      } else {
        // Run new analysis
        message.loading({
          content: `Analizando ${documentTitle}...`,
          key: "doc-analysis",
          duration: 0,
        });
        analysis = await documentAPI.analyze(documentId);
      }

      if (analysis.error) {
        if (
          analysis.error_type === "quota_exceeded" ||
          analysis.error_type === "rate_limit"
        ) {
          message.error({
            content:
              analysis.error ||
              "Cuota de API agotada. Por favor, verifica tu configuración.",
            key: "doc-analysis",
            duration: 5,
          });
        } else {
          message.error({
            content: analysis.error || "Error al analizar el documento",
            key: "doc-analysis",
            duration: 5,
          });
        }
        setAnalyzingDocument(null);
        return;
      }

      setDocumentAnalysis({ documentId, documentTitle, analysis });
      setAnalysisModalVisible(true);

      // Update saved analyses map
      if (analysis.analysis_id) {
        setDocumentAnalyses((prev) => new Map(prev).set(documentId, true));
      }

      message.success({
        content: useSaved
          ? "Análisis cargado"
          : "Análisis completado y guardado",
        key: "doc-analysis",
      });
    } catch (error: any) {
      console.error("Error analyzing document:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Error desconocido";
      if (error?.response?.status === 429) {
        message.error({
          content:
            "Demasiadas solicitudes. Por favor, espera unos momentos y vuelve a intentar.",
          key: "doc-analysis",
          duration: 5,
        });
      } else if (error?.response?.status === 404 && useSaved) {
        message.info({
          content: "No hay análisis guardado. Ejecutando nuevo análisis...",
          key: "doc-analysis",
          duration: 3,
        });
        // Try running new analysis instead
        await handleDocumentAnalysis(documentId, documentTitle, false);
        return;
      } else {
        message.error({
          content: `Error: ${errorMsg}`,
          key: "doc-analysis",
          duration: 5,
        });
      }
    } finally {
      setAnalyzingDocument(null);
    }
  };

  if (loading) return <Spin size="large" />;
  if (!case_) return <div>Caso no encontrado</div>;

  return (
    <div>
      <Card
        title={
          <span style={{ fontSize: "24px" }}>📁 Caso {case_.case_number}</span>
        }
        extra={
          <Space>
            <Tag color={case_.priority === "high" ? "red" : "blue"}>
              {case_.priority?.toUpperCase()}
            </Tag>
            <Tag color={case_.status === "active" ? "green" : "default"}>
              {case_.status?.toUpperCase()}
            </Tag>
          </Space>
        }
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Tipo">{case_.case_type}</Descriptions.Item>
          <Descriptions.Item label="Jurisdicción">
            {case_.jurisdiction}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha de Presentación">
            {dayjs(case_.filing_date).format("DD/MM/YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Estado">{case_.status}</Descriptions.Item>
          <Descriptions.Item label="Resumen" span={2}>
            {case_.summary}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs defaultActiveKey="1" style={{ marginTop: 24 }}>
        <TabPane tab="👥 Partes" key="1">
          <Card>
            {parties.map((party) => (
              <Card.Grid key={party.party_id} style={{ width: "50%" }}>
                <p>
                  <strong>Nombre:</strong> {party.full_name}
                </p>
                <p>
                  <strong>Tipo:</strong> <Tag>{party.party_type}</Tag>
                </p>
                {party.id_number && (
                  <p>
                    <strong>C.I.:</strong> {party.id_number}
                  </p>
                )}
                {party.role && (
                  <p>
                    <strong>Rol:</strong> {party.role}
                  </p>
                )}
              </Card.Grid>
            ))}
          </Card>
        </TabPane>

        <TabPane tab="📅 Cronología" key="2">
          <Card>
            <Timeline
              items={events
                .sort(
                  (a, b) =>
                    new Date(a.event_date).getTime() -
                    new Date(b.event_date).getTime()
                )
                .map((event) => ({
                  children: (
                    <div>
                      <div style={{ fontWeight: "bold" }}>
                        {dayjs(event.event_date).format("DD/MM/YYYY")} -{" "}
                        {event.event_type}
                      </div>
                      <div>{event.description}</div>
                      {event.outcome && (
                        <div style={{ fontStyle: "italic" }}>
                          Resultado: {event.outcome}
                        </div>
                      )}
                    </div>
                  ),
                }))}
            />
          </Card>
        </TabPane>

        <TabPane tab="📄 Documentos" key="3">
          <Card
            extra={
              <Space wrap>
                <Button
                  icon={<DatabaseOutlined />}
                  loading={copyingAllPdfs}
                  onClick={async () => {
                    if (!caseId) return;
                    setCopyingAllPdfs(true);
                    try {
                      const res = await documentAPI.copyCasePdfsToDb1995(
                        parseInt(caseId)
                      );
                      message.success(
                        `db1995 actualizado: ${res.new_rows} nuevos, ${res.updated_rows} actualizados`
                      );
                    } catch (e: any) {
                      message.error(
                        e?.response?.data?.detail ||
                          "Error al copiar PDFs a db1995"
                      );
                    } finally {
                      setCopyingAllPdfs(false);
                    }
                  }}
                >
                  Copiar PDFs a db1995
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={async () => {
                    if (!caseId) return;
                    try {
                      message.loading({
                        content: "Cargando último análisis combinado guardado...",
                        key: "combined-load",
                        duration: 0,
                      });
                      const saved = await aiAPI.getCombinedAnalysis(parseInt(caseId));
                      setDocumentAnalysis({
                        documentId: -1,
                        documentTitle: `Análisis Combinado (${new Date(saved.analysis_date).toLocaleDateString("es-ES")})`,
                        analysis: { ...saved.results, saved_at: saved.analysis_date },
                      });
                      setAnalysisModalVisible(true);
                      message.success({
                        content: "Análisis cargado",
                        key: "combined-load",
                      });
                    } catch (e: any) {
                      if (e?.response?.status === 404) {
                        message.info({
                          content: "No hay análisis combinado guardado. Ejecuta uno primero.",
                          key: "combined-load",
                        });
                      } else {
                        message.error({
                          content: e?.response?.data?.detail || "Error al cargar",
                          key: "combined-load",
                        });
                      }
                    }
                  }}
                >
                  Ver último análisis
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => {
                    if (!caseId) return;
                    window.open(
                      aiAPI.getOcrExportUrl(parseInt(caseId)),
                      "_blank"
                    );
                    message.success("Descargando OCR en formato Markdown...");
                  }}
                >
                  Descargar OCR (MD)
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={async () => {
                    if (!caseId) return;
                    try {
                      message.loading({
                        content: "Extrayendo autoridades y agregando a MAC...",
                        key: "mac-extract",
                        duration: 0,
                      });
                      const res = await aiAPI.extractAuthoritiesToMac(parseInt(caseId));
                      message.success({
                        content: `Agregadas ${res.added} autoridades a MAC (mac.5bc.us)`,
                        key: "mac-extract",
                        duration: 5,
                      });
                    } catch (e: any) {
                      message.error({
                        content: e?.response?.data?.detail || "Error al extraer a MAC",
                        key: "mac-extract",
                      });
                    }
                  }}
                >
                  Extraer a MAC
                </Button>
                {selectedDocuments.length > 0 && (
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    onClick={() => handleCombinedAnalysis(selectedDocuments)}
                    loading={analyzingDocument === -1}
                  >
                    Analizar {selectedDocuments.length} Documento
                    {selectedDocuments.length > 1 ? "s" : ""} Seleccionado
                    {selectedDocuments.length > 1 ? "s" : ""}
                  </Button>
                )}
                <Upload
                  name="file"
                  multiple
                  action={`${process.env.REACT_APP_API_URL || "https://legal.5bc.us/api"}/documents/upload`}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff"
                  data={{
                    case_id: parseInt(caseId || "0"),
                    document_type: "poder",
                  }}
                  onChange={(info) => {
                    const { file, fileList } = info;
                    const active = fileList.filter(
                      (f) => f.originFileObj != null && f.status !== "removed"
                    );
                    const uploading = active.some(
                      (f) => f.status === "uploading"
                    );
                    const doneOk = active.filter((f) => f.status === "done");
                    const doneErr = active.filter((f) => f.status === "error");
                    const allSettled =
                      active.length > 0 &&
                      !uploading &&
                      doneOk.length + doneErr.length === active.length;

                    if (file.status === "error") {
                      message.error(`${file.name}: falló al subir`);
                    }

                    if (allSettled) {
                      if (doneOk.length > 0) {
                        const tiffConverted = doneOk.filter((f) => {
                          const r = f.response as
                            | { converted_from_tiff?: boolean }
                            | undefined;
                          return r?.converted_from_tiff;
                        }).length;
                        const n = doneOk.length;
                        let okMsg =
                          n === 1
                            ? `${doneOk[0].name} subido correctamente`
                            : `${n} archivos subidos correctamente`;
                        if (tiffConverted > 0) {
                          okMsg +=
                            tiffConverted === 1
                              ? " (1 TIFF → PDF)"
                              : ` (${tiffConverted} TIFF → PDF)`;
                        }
                        message.success(okMsg);
                      }
                      if (caseId) fetchCaseData(parseInt(caseId));
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    Subir documentos (varios)
                  </Button>
                </Upload>
              </Space>
            }
          >
            <Table
              dataSource={documents}
              rowKey="document_id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} documentos`,
              }}
              rowSelection={{
                selectedRowKeys: selectedDocuments,
                preserveSelectedRowKeys: true,
                onChange: (selectedRowKeys) => {
                  setSelectedDocuments(selectedRowKeys);
                },
              }}
              columns={[
                {
                  title: "Título",
                  dataIndex: "title",
                  key: "title",
                  render: (text: string, record: any) => (
                    <a
                      href={`https://legal.5bc.us/api/documents/download/${record.document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {text}
                    </a>
                  ),
                },
                {
                  title: "Tipo",
                  dataIndex: "document_type",
                  key: "document_type",
                },
                {
                  title: "Vinculado a",
                  dataIndex: "event_id",
                  key: "event_id",
                  render: (eventId: number | null, record: any) => (
                    <Select
                      placeholder="Vincular a evento"
                      allowClear
                      value={eventId || undefined}
                      style={{ width: 200 }}
                      onChange={async (val) => {
                        try {
                          await documentAPI.update(record.document_id, {
                            event_id: val ?? null,
                          });
                          message.success("Documento vinculado");
                          if (caseId) fetchCaseData(parseInt(caseId));
                        } catch {
                          message.error("Error al vincular");
                        }
                      }}
                      options={events.map((e) => ({
                        value: e.event_id,
                        label: `${dayjs(e.event_date).format("DD/MM/YYYY")} - ${e.event_type}`,
                      }))}
                    />
                  ),
                },
                {
                  title: "Fecha",
                  dataIndex: "upload_date",
                  key: "upload_date",
                  render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
                },
                {
                  title: "Estado",
                  dataIndex: "status",
                  key: "status",
                  render: (status: string) => <Tag>{status}</Tag>,
                },
                {
                  title: "Acciones",
                  key: "actions",
                  render: (_: any, record: any) => {
                    const hasSavedAnalysis = documentAnalyses.get(
                      record.document_id
                    );
                    const isTiff = /\.tiff?$/i.test(record.title || "");
                    return (
                      <Space>
                        <Button
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          href={`https://legal.5bc.us/api/documents/download/${record.document_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          icon={<DatabaseOutlined />}
                          loading={copyingDocId === record.document_id}
                          onClick={async () => {
                            setCopyingDocId(record.document_id);
                            try {
                              await documentAPI.copyToDb1995(record.document_id);
                              message.success(
                                `${record.title} copiado a db1995`
                              );
                            } catch (e: any) {
                              message.error(
                                e?.response?.data?.detail ||
                                  "Error al copiar a db1995"
                              );
                            } finally {
                              setCopyingDocId(null);
                            }
                          }}
                        >
                          db1995
                        </Button>
                        {isTiff && (
                          <Button
                            type="link"
                            size="small"
                            onClick={async () => {
                              try {
                                message.loading({
                                  content: "Convirtiendo TIFF a PDF…",
                                  key: "tiff-pdf",
                                  duration: 0,
                                });
                                await documentAPI.convertTiffToPdf(
                                  record.document_id
                                );
                                message.success({
                                  content: "Documento convertido a PDF",
                                  key: "tiff-pdf",
                                });
                                if (caseId) fetchCaseData(parseInt(caseId));
                              } catch (e: any) {
                                message.error({
                                  content:
                                    e?.response?.data?.detail ||
                                    "Error al convertir",
                                  key: "tiff-pdf",
                                });
                              }
                            }}
                          >
                            TIFF → PDF
                          </Button>
                        )}
                        {hasSavedAnalysis && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              handleDocumentAnalysis(
                                record.document_id,
                                record.title,
                                true
                              )
                            }
                            title="Ver análisis guardado"
                          >
                            Ver Análisis
                          </Button>
                        )}
                        <Button
                          type="text"
                          icon={<RobotOutlined />}
                          loading={analyzingDocument === record.document_id}
                          onClick={() =>
                            handleDocumentAnalysis(
                              record.document_id,
                              record.title,
                              false
                            )
                          }
                          title="Obtener asesoramiento legal"
                        >
                          {hasSavedAnalysis ? "Re-analizar" : "Analizar"}
                        </Button>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={async () => {
                            try {
                              await documentAPI.delete(record.document_id);
                              message.success(
                                "Documento eliminado exitosamente"
                              );
                              if (caseId) fetchCaseData(parseInt(caseId));
                            } catch (error) {
                              message.error("Error al eliminar documento");
                              console.error("Error deleting document:", error);
                            }
                          }}
                        />
                      </Space>
                    );
                  },
                },
              ]}
            />
          </Card>
        </TabPane>

        <TabPane tab="🔍 Pruebas" key="4">
          <Card>
            <Table
              dataSource={evidence}
              rowKey="evidence_id"
              columns={[
                {
                  title: "Tipo",
                  dataIndex: "evidence_type",
                  key: "evidence_type",
                },
                {
                  title: "Descripción",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Fecha de Recolección",
                  dataIndex: "collection_date",
                  key: "collection_date",
                  render: (date: string) =>
                    date ? dayjs(date).format("DD/MM/YYYY") : "N/A",
                },
                {
                  title: "Estado",
                  dataIndex: "status",
                  key: "status",
                  render: (status: string) => <Tag>{status}</Tag>,
                },
                {
                  title: "Acciones",
                  key: "actions",
                  render: (_: any, record: any) => (
                    <Space>
                      {record.file_path ? (
                        <>
                          <a
                            href={evidenceAPI.getDownloadUrl(record.evidence_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </a>
                          <Button
                            type="link"
                            size="small"
                            icon={<RobotOutlined />}
                            onClick={async () => {
                              setAnalyzingDocument(record.evidence_id + 10000);
                              try {
                                const analysis = await evidenceAPI.analyze(
                                  record.evidence_id
                                );
                                setDocumentAnalysis({
                                  documentId: record.evidence_id,
                                  documentTitle: record.description || "Prueba",
                                  analysis,
                                });
                                setAnalysisModalVisible(true);
                                message.success("Análisis completado");
                              } catch (e: any) {
                                message.error(
                                  e?.response?.data?.detail || "Error al analizar"
                                );
                              } finally {
                                setAnalyzingDocument(null);
                              }
                            }}
                            loading={
                              analyzingDocument === record.evidence_id + 10000
                            }
                          >
                            Analizar
                          </Button>
                        </>
                      ) : (
                        <Upload
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          showUploadList={false}
                          customRequest={async ({ file }) => {
                            try {
                              await evidenceAPI.uploadFile(
                                record.evidence_id,
                                file as File
                              );
                              message.success("Archivo adjuntado");
                              if (caseId) fetchCaseData(parseInt(caseId));
                            } catch {
                              message.error("Error al subir");
                            }
                          }}
                        >
                          <Button type="link" size="small" icon={<UploadOutlined />}>
                            Adjuntar archivo
                          </Button>
                        </Upload>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </TabPane>

        <TabPane tab="🤖 Análisis IA" key="5">
          <Card
            extra={
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={handleAIAnalysis}
              >
                Ejecutar Análisis
              </Button>
            }
          >
            {aiAnalysis ? (
              <div>
                <Card title="Análisis de Riesgo" style={{ marginBottom: 16 }}>
                  <pre>{JSON.stringify(aiAnalysis.risk, null, 2)}</pre>
                </Card>
                <Card title="Omisiones Detectadas" style={{ marginBottom: 16 }}>
                  <pre>{JSON.stringify(aiAnalysis.omissions, null, 2)}</pre>
                </Card>
                <Card title="Precedentes Relevantes">
                  <pre>{JSON.stringify(aiAnalysis.precedents, null, 2)}</pre>
                </Card>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FileTextOutlined style={{ fontSize: "48px", color: "#ccc" }} />
                <p>Ejecuta el análisis de IA para ver insights automáticos</p>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={
          documentAnalysis ? (
            <span>
              🤖 Asesoramiento Legal: {documentAnalysis.documentTitle}
              {documentAnalysis.analysis?.saved_at && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginLeft: "10px",
                  }}
                >
                  (Guardado:{" "}
                  {new Date(documentAnalysis.analysis.saved_at).toLocaleString(
                    "es-ES"
                  )}
                  )
                </span>
              )}
            </span>
          ) : (
            "Análisis de Documento"
          )
        }
        open={analysisModalVisible}
        onCancel={() => {
          setAnalysisModalVisible(false);
          setDocumentAnalysis(null);
        }}
        footer={[
          <Button
            key="view"
            icon={<FileTextOutlined />}
            onClick={() => {
              if (!documentAnalysis?.analysis) return;
              const viewWindow = window.open("", "_blank");
              if (!viewWindow) {
                message.warning("Permite ventanas emergentes para ver el informe");
                return;
              }
              const title = (documentAnalysis.documentTitle || "Análisis Legal")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
              const jsonB64 = btoa(
                unescape(encodeURIComponent(JSON.stringify(documentAnalysis.analysis)))
              );
              const html = getReportHtml(title, jsonB64);
              viewWindow.document.write(html);
              viewWindow.document.close();
              viewWindow.focus();
            }}
          >
            Ver informe
          </Button>,
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={() => {
              if (!documentAnalysis?.analysis) return;
              const printWindow = window.open("", "_blank");
              if (!printWindow) {
                message.warning("Permite ventanas emergentes para imprimir");
                return;
              }
              const title = (documentAnalysis.documentTitle || "Análisis Legal")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
              const jsonB64 = btoa(
                unescape(encodeURIComponent(JSON.stringify(documentAnalysis.analysis)))
              );
              const html = getReportHtml(title, jsonB64);
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.focus();
              setTimeout(() => {
                printWindow.print();
                printWindow.close();
              }, 500);
            }}
          >
            Imprimir
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setAnalysisModalVisible(false);
              setDocumentAnalysis(null);
            }}
          >
            Cerrar
          </Button>,
        ]}
        width={800}
      >
        {documentAnalysis && documentAnalysis.analysis ? (
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {documentAnalysis.analysis.error ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <p style={{ color: "red", fontSize: "16px" }}>
                  {documentAnalysis.analysis.error}
                </p>
                {documentAnalysis.analysis.error_type === "quota_exceeded" && (
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    Por favor, verifica tu configuración de OpenAI o contacta al
                    administrador.
                  </p>
                )}
              </div>
            ) : (
              <div>
                {/* Helper function to render nested objects */}
                {(() => {
                  const renderValue = (
                    value: any,
                    depth: number = 0
                  ): React.ReactNode => {
                    if (value === null || value === undefined)
                      return <span style={{ color: "#999" }}>N/A</span>;

                    if (typeof value === "string") {
                      return <span>{value}</span>;
                    }

                    if (
                      typeof value === "number" ||
                      typeof value === "boolean"
                    ) {
                      return <span>{String(value)}</span>;
                    }

                    if (Array.isArray(value)) {
                      if (value.length === 0)
                        return <span style={{ color: "#999" }}>Ninguno</span>;
                      return (
                        <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                          {value.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: "8px" }}>
                              {renderValue(item, depth + 1)}
                            </li>
                          ))}
                        </ul>
                      );
                    }

                    if (typeof value === "object") {
                      return (
                        <div style={{ marginLeft: depth > 0 ? "16px" : "0" }}>
                          {Object.entries(value).map(([key, val]) => (
                            <div key={key} style={{ marginBottom: "12px" }}>
                              <strong
                                style={{
                                  color: "#1890ff",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                {key}:
                              </strong>
                              <div
                                style={{ marginLeft: "16px", color: "#333" }}
                              >
                                {renderValue(val, depth + 1)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return <span>{String(value)}</span>;
                  };

                  const analisisDoc =
                    documentAnalysis.analysis.analisis_documento ||
                    documentAnalysis.analysis["Análisis del Documento"];
                  const asesoramiento =
                    documentAnalysis.analysis.asesoramiento_legal ||
                    documentAnalysis.analysis["Asesoramiento Legal"];
                  const recomendaciones =
                    documentAnalysis.analysis.recomendaciones_especificas ||
                    documentAnalysis.analysis["Recomendaciones Específicas"];
                  const referencias =
                    documentAnalysis.analysis.referencias_legales ||
                    documentAnalysis.analysis["Referencias Legales"];

                  return (
                    <>
                      {analisisDoc && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              📄 Análisis del Documento
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(analisisDoc)}
                          </div>
                        </Card>
                      )}

                      {asesoramiento && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              ⚖️ Asesoramiento Legal
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(asesoramiento)}
                          </div>
                        </Card>
                      )}

                      {recomendaciones && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              💡 Recomendaciones Específicas
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(recomendaciones)}
                          </div>
                        </Card>
                      )}

                      {referencias && (
                        <Card
                          title={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              📚 Referencias Legales
                            </span>
                          }
                          style={{
                            marginBottom: 16,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div style={{ padding: "8px 0" }}>
                            {renderValue(referencias)}
                          </div>
                        </Card>
                      )}

                      {/* Fallback if no structured sections */}
                      {!analisisDoc &&
                        !asesoramiento &&
                        !recomendaciones &&
                        !referencias && (
                          <Card title="📄 Análisis Completo">
                            <div style={{ padding: "8px 0" }}>
                              {renderValue(documentAnalysis.analysis)}
                            </div>
                          </Card>
                        )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ) : (
          <Spin />
        )}
      </Modal>
    </div>
  );
};

export default CaseView;
